import { CheckCircleIcon } from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider/Divider";
import { formatCurrency } from "@/utils/currency";

import type { ExtractedFlightFareDetails } from "./extractDataFromFlightFare";
import { FlightFareBaggageInfo } from "./sections/FlightFareBaggageInfo";
import { FlightFareChangeRefundInfo } from "./sections/FlightFareChangeRefundInfo";
import { FlightFareOtherBenefitsInfo } from "./sections/FlightFareOtherBenefitsInfo";

type FlightFareCardProps = {
  fare: ExtractedFlightFareDetails;
  isSelected?: boolean;
  onSelect?: () => void;
  priceDifference?: number;
  currency?: string;
};

export function FlightFareCard({
  fare,
  isSelected,
  onSelect,
  priceDifference,
  currency,
}: FlightFareCardProps) {
  const intl = useIntl();

  return (
    <div className="section-card flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-text-title uppercase">
          {fare.name}
        </span>

        {priceDifference !== undefined && (
          <bdi>
            <span className="text-base font-semibold text-text-primary">
              {priceDifference >= 0 ? "+" : ""}
              {formatCurrency(priceDifference, currency)}
            </span>
          </bdi>
        )}
      </div>

      <Divider color="neutral-soft" />

      <div className="flex flex-col gap-4">
        {!!fare.baggage && <FlightFareBaggageInfo baggage={fare.baggage} />}

        {(!!fare.flightChange || !!fare.flightRefund) && (
          <FlightFareChangeRefundInfo
            changeInfo={fare.flightChange}
            refundInfo={fare.flightRefund}
          />
        )}

        {(!!fare.loyaltyProgram || !!fare.priority || !!fare.loungeAccess) && (
          <FlightFareOtherBenefitsInfo
            loyaltyProgram={fare.loyaltyProgram}
            priority={fare.priority}
            loungeAccess={fare.loungeAccess}
          />
        )}
      </div>

      {onSelect !== undefined && (
        <div className="pt-2">
          <Button
            fullWidth
            variant={isSelected ? "contained" : "outlined"}
            startIcon={isSelected ? <CheckCircleIcon size={24} /> : undefined}
            onClick={onSelect}
          >
            {isSelected
              ? intl.formatMessage({
                  defaultMessage: "Selected",
                  description: "Label for fare card when this fare is selected",
                })
              : intl.formatMessage({
                  defaultMessage: "Select",
                  description: "Button label to select a fare card",
                })}
          </Button>
        </div>
      )}
    </div>
  );
}
